import merge from 'lodash/merge';
import type { Device } from '../../types/client-data';
import type { DataLayerBase } from '../data-layer-service';
import { parseDatalayer } from './datalayer-parser';

export type PageViewPayload = {
    pageType?: 'ResultsBrowse' | string;
    platform?: string;
    deviceSize?: Device;
    vehicle?: {
        make?: string;
        model?: string;
        transmission?: string;
        fuelType?: string;
    };
    pets?: {
        catBreed?: string | undefined;
        dogBreed?: string | undefined;
        isDeflead?: boolean | undefined;
        isVaccinated?: boolean;
        isMicrochipped?: boolean;
        isNeuteredOrSpayed?: boolean;
    };
    category?: {
        l0?: { id: string | undefined };
        l1?: { id: string | undefined };
        l2?: { id: string | undefined };
        l3?: { id: string | undefined };
        l4?: { id: string | undefined };
    };
    location?: {
        l0?: { id: string | undefined };
        l1?: { id: string | undefined };
        l2?: { id: string | undefined };
        l3?: { id: string | undefined };
        l4?: { id: string | undefined };
    };
    user?: {
        loggedIn?: boolean;
        age?: number;
    };
    ad?: {
        id?: string;
        user?: {
            type?: string;
        };
        sellerType?: string;
        price?: {
            amount?: number;
            currency?: string;
        };
        descriptionLength?: number;
        description?: string;
        imageCount?: number;
        creationDate?: string | number;
        lastPublishedDate?: string | number;
        advertClickSource?: string;
    };
    search?: {
        type?: 'Browse' | 'Search';
        keyword?: string;
        pageNumber?: number;
        relatedSearch?: string[];
        totalResults?: number;
    };
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

export const stripUndefinedFields = <T>(value: T): T | undefined => {
    if (Array.isArray(value)) {
        return value.map((v) => stripUndefinedFields(v)) as T;
    }

    if (!isPlainObject(value)) {
        return value !== undefined ? value : undefined;
    }

    const result: Record<string, unknown> = {};

    for (const [key, val] of Object.entries(value)) {
        const strippedValue = stripUndefinedFields(val);
        if (
            strippedValue !== undefined &&
            !(isPlainObject(strippedValue) && Object.keys(strippedValue).length === 0)
        ) {
            result[key] = strippedValue;
        }
    }

    return Object.keys(result).length > 0 ? (result as T) : undefined;
};

export const upsertPageViewPayload = (
    delta: Partial<PageViewPayload>,
    existingPayload: PageViewPayload
): PageViewPayload => {
    const cleanedDelta = stripUndefinedFields(delta) ?? {};
    return merge({}, existingPayload, cleanedDelta); // deepMerge
};

export const generatePageViewPayload = ({
    dataLayerBase,
    additional,
}: {
    dataLayerBase: DataLayerBase;
    additional: Partial<PageViewPayload>;
}): PageViewPayload => {
    return upsertPageViewPayload(additional, parseDatalayer(dataLayerBase));
};

export const generateActionPayload = ({
    dataLayerBase,
    additional,
    actionName,
}: {
    dataLayerBase: DataLayerBase;
    additional: Partial<PageViewPayload>;
    actionName: string;
}): PageViewPayload & { actionName: string } => {
    const pageViewPayload = generatePageViewPayload({ dataLayerBase, additional });
    return { ...pageViewPayload, actionName };
};
